import React, { useState, useMemo } from 'react';
import { BookOpen, GraduationCap, Users, Plus, X, Search, Play, FileText, ChevronDown } from 'lucide-react';
import { safeAsync, Badge, KPI, emptyMsg, btnPrimary, btnSm, btnDanger, cardWrap, neuShadow, neuShadowSm, inp, modalStyle, overlayStyle, modalHeader, modalTitle, closeBtn } from './crmShared';
import { matchesNameQuery } from '../../lib/nameSearch';

const COURSE_STATUS_COLORS = { draft: '#F26522', published: '#62BB46' };
const ACCESS_TYPE_COLORS = { free: '#4FA038', paid: '#2E8B98', subscription: '#256F7A' };
const ENROLLMENT_STATUS_COLORS = { pending: '#F26522', active: '#62BB46', completed: '#4FA038', dropped: '#6C757D' };

export default function MembershipsTab({ contacts, canCreate, canUpdate }) {
  const [tab, setTab] = useState('courses'); // 'courses' | 'enrollments'
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedCourseForEnroll, setSelectedCourseForEnroll] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [courseForm, setCourseForm] = useState({ title: '', description: '', category: '', access_type: 'free', price: 0, status: 'draft', modules: [] });
  const [currentModule, setCurrentModule] = useState(null);
  const [enrollForm, setEnrollForm] = useState({ contact_id: '', course_id: '', progress: 0 });

  // Load data on mount.
  // Coerce every IPC response with Array.isArray() — bare `c || []` keeps
  // truthy non-array values (e.g. an error envelope like { error: ... }),
  // which then crashes the .filter() / .reduce() calls below with
  // "x.filter is not a function". This guard makes the tab robust to any
  // response shape the IPC layer ever returns.
  React.useEffect(() => {
    safeAsync(async () => {
      const c = await window.zignagenixAPI.getCRMCourses({});
      setCourses(Array.isArray(c) ? c : []);
      const e = await window.zignagenixAPI.getCRMEnrollments({});
      setEnrollments(Array.isArray(e) ? e : []);
    })();
  }, []);

  // ─── Course Modal Handlers ─────────────────────────────────────────
  const openCourseModal = (course = null) => {
    if (course) {
      setEditingCourse(course.id);
      setCourseForm(course);
    } else {
      setCourseForm({ title: '', description: '', category: '', access_type: 'free', price: 0, status: 'draft', modules: [] });
    }
    setCurrentModule(null);
    setShowCourseModal(true);
  };

  const saveCourse = safeAsync(async () => {
    if (!courseForm.title.trim()) { alert('Title required'); return; }
    const data = { ...courseForm };
    if (editingCourse) {
      await window.zignagenixAPI.updateCRMCourse(editingCourse, data);
    } else {
      await window.zignagenixAPI.createCRMCourse(data);
    }
    const c = await window.zignagenixAPI.getCRMCourses({});
    setCourses(Array.isArray(c) ? c : []);
    setShowCourseModal(false);
    setEditingCourse(null);
  });

  const addModule = () => {
    setCourseForm(prev => ({
      ...prev,
      modules: [...prev.modules, { id: Date.now(), title: '', lessons: [] }]
    }));
  };

  const addLesson = (moduleId) => {
    setCourseForm(prev => ({
      ...prev,
      modules: prev.modules.map(m => m.id === moduleId ? {
        ...m,
        lessons: [...m.lessons, { id: Date.now(), title: '', content_type: 'video', content_url: '', duration: '' }]
      } : m)
    }));
  };

  const updateModule = (moduleId, field, value) => {
    setCourseForm(prev => ({
      ...prev,
      modules: prev.modules.map(m => m.id === moduleId ? { ...m, [field]: value } : m)
    }));
  };

  const updateLesson = (moduleId, lessonId, field, value) => {
    setCourseForm(prev => ({
      ...prev,
      modules: prev.modules.map(m => m.id === moduleId ? {
        ...m,
        lessons: m.lessons.map(l => l.id === lessonId ? { ...l, [field]: value } : l)
      } : m)
    }));
  };

  const removeModule = (moduleId) => {
    setCourseForm(prev => ({
      ...prev,
      modules: prev.modules.filter(m => m.id !== moduleId)
    }));
  };

  const removeLesson = (moduleId, lessonId) => {
    setCourseForm(prev => ({
      ...prev,
      modules: prev.modules.map(m => m.id === moduleId ? {
        ...m,
        lessons: m.lessons.filter(l => l.id !== lessonId)
      } : m)
    }));
  };

  // ─── Enrollment Handlers ───────────────────────────────────────────
  const openEnrollModal = (course) => {
    setSelectedCourseForEnroll(course.id);
    setEnrollForm({ contact_id: '', course_id: course.id, progress: 0 });
    setShowEnrollModal(true);
  };

  const saveEnrollment = safeAsync(async () => {
    if (!enrollForm.contact_id) { alert('Select contact'); return; }
    await window.zignagenixAPI.createCRMEnrollment(enrollForm);
    const e = await window.zignagenixAPI.getCRMEnrollments({});
    setEnrollments(e);
    setShowEnrollModal(false);
  });

  const updateEnrollmentProgress = safeAsync(async (id, progress) => {
    await window.zignagenixAPI.updateCRMEnrollmentProgress(id, { progress });
    const e = await window.zignagenixAPI.getCRMEnrollments({});
    setEnrollments(e);
  });

  // ─── KPIs ─────────────────────────────────────────────────────────
  const totalCourses = courses.length;
  const activeEnrollments = enrollments.filter(e => e.status === 'active').length;
  const completedCount = enrollments.filter(e => e.status === 'completed').length;
  const completionRate = enrollments.length > 0 ? Math.round(completedCount / enrollments.length * 100) : 0;
  const totalRevenue = courses.filter(c => c.access_type === 'paid').reduce((sum, c) => sum + (c.price || 0) * (enrollments.filter(e => e.course_id === c.id).length), 0);

  // ─── Filtering ─────────────────────────────────────────────────────
  const filteredCourses = useMemo(() => {
    if (!searchQuery) return courses;
    const q = searchQuery.toLowerCase();
    return courses.filter(c => (c.title && c.title.toLowerCase().includes(q)) || (c.description && c.description.toLowerCase().includes(q)));
  }, [courses, searchQuery]);

  const filteredEnrollments = useMemo(() => {
    if (!searchQuery) return enrollments;
    const q = searchQuery.toLowerCase();
    return enrollments.filter(e => {
      const contact = contacts.find(c => c.id === e.contact_id);
      const course = courses.find(c => c.id === e.course_id);
      // ⚠ last_name was missing here entirely before this card — searching an
      // enrollee by surname alone matched nothing. Both parts now participate.
      return (contact && matchesNameQuery([contact.first_name, contact.last_name], q)) ||
             (course && matchesNameQuery([course.title], q));
    });
  }, [enrollments, searchQuery, contacts, courses]);

  return (
    <div>
      {/* KPIs */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <KPI icon={BookOpen} label="Total Courses" value={totalCourses} color="#2E8B98" />
        <KPI icon={Users} label="Active Enrollments" value={activeEnrollments} color="#62BB46" />
        <KPI icon={GraduationCap} label="Completion Rate" value={`${completionRate}%`} color="#4FA038" />
        <KPI icon={Play} label="Revenue" value={`$${totalRevenue.toFixed(0)}`} color="#F26522" />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, borderBottom: '2px solid #E8EBF0', paddingBottom: 0 }}>
        <button
          onClick={() => setTab('courses')}
          style={{
            padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
            color: tab === 'courses' ? '#3CA0AE' : '#6C757D', borderBottom: tab === 'courses' ? '3px solid #3CA0AE' : 'none', marginBottom: -2
          }}
        >
          <BookOpen size={14} style={{ marginRight: 6, display: 'inline' }} /> Courses
        </button>
        <button
          onClick={() => setTab('enrollments')}
          style={{
            padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
            color: tab === 'enrollments' ? '#3CA0AE' : '#6C757D', borderBottom: tab === 'enrollments' ? '3px solid #3CA0AE' : 'none', marginBottom: -2
          }}
        >
          <Users size={14} style={{ marginRight: 6, display: 'inline' }} /> Enrollments
        </button>
      </div>

      {/* Search Bar */}
      {(tab === 'courses' ? courses.length : enrollments.length) > 0 && (
        <div style={{ marginBottom: 16, position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: 12, color: '#ADB5BD' }} />
          <input
            type="text"
            placeholder={tab === 'courses' ? 'Search courses...' : 'Search enrollments...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ ...inp, paddingLeft: 36, paddingRight: searchQuery ? 36 : 12 }}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} style={{ position: 'absolute', right: 10, top: 12, background: 'none', border: 'none', cursor: 'pointer', color: '#ADB5BD' }}>
              <X size={16} />
            </button>
          )}
        </div>
      )}

      {/* ─── COURSES TAB ─────────────────────────────────────────────── */}
      {tab === 'courses' && (
        <div>
          {canCreate && (
            <button onClick={() => openCourseModal()} style={{ ...btnPrimary, marginBottom: 16 }}>
              <Plus size={13} /> Create Course
            </button>
          )}
          {filteredCourses.length === 0 ? (
            <div style={{ ...cardWrap, ...emptyMsg }}>No courses yet</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
              {filteredCourses.map(c => (
                <div key={c.id} style={{ ...cardWrap, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ height: 140, background: '#E8EBF0', borderRadius: 10, marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ADB5BD' }}>
                    <BookOpen size={32} />
                  </div>
                  <h4 style={{ fontSize: 13, fontWeight: 700, margin: '0 0 6px 0', color: '#212529' }}>{c.title}</h4>
                  <p style={{ fontSize: 11, color: '#6C757D', margin: '0 0 10px 0', flex: 1 }}>{c.description || 'No description'}</p>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                    <Badge text={c.access_type} color={ACCESS_TYPE_COLORS[c.access_type] || '#6C757D'} />
                    <Badge text={c.status} color={COURSE_STATUS_COLORS[c.status] || '#6C757D'} />
                  </div>
                  {c.access_type !== 'free' && <div style={{ fontSize: 12, fontWeight: 600, color: '#3CA0AE', marginBottom: 8 }}>${c.price}</div>}
                  <div style={{ fontSize: 10, color: '#ADB5BD', marginBottom: 12 }}>{enrollments.filter(e => e.course_id === c.id).length} enrolled</div>
                  {canUpdate && (
                    <button onClick={() => openCourseModal(c)} style={{ ...btnSm, width: '100%' }}>
                      Edit Course
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── ENROLLMENTS TAB ──────────────────────────────────────────── */}
      {tab === 'enrollments' && (
        <div>
          {filteredEnrollments.length === 0 ? (
            <div style={{ ...cardWrap, ...emptyMsg }}>No enrollments yet</div>
          ) : (
            <div style={{ ...cardWrap, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead style={{ background: '#E8EBF0', borderBottom: '2px solid #D4D8E0' }}>
                  <tr>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6C757D' }}>Contact</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6C757D' }}>Course</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6C757D' }}>Status</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6C757D' }}>Progress</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6C757D' }}>Enrolled</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEnrollments.map(e => {
                    const contact = contacts.find(c => c.id === e.contact_id);
                    const course = courses.find(c => c.id === e.course_id);
                    return (
                      <tr key={e.id} style={{ borderBottom: '1px solid #E8EBF0' }}>
                        <td style={{ padding: '10px 12px', color: '#212529' }}>{contact ? `${contact.first_name} ${contact.last_name}` : 'Unknown'}</td>
                        <td style={{ padding: '10px 12px', color: '#212529' }}>{course ? course.title : 'Unknown'}</td>
                        <td style={{ padding: '10px 12px' }}>
                          <Badge text={e.status} color={ENROLLMENT_STATUS_COLORS[e.status] || '#6C757D'} />
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ flex: 1, height: 6, background: '#E8EBF0', borderRadius: 3, overflow: 'hidden' }}>
                              <div style={{ height: '100%', background: '#62BB46', width: `${e.progress || 0}%`, transition: 'width 0.3s' }} />
                            </div>
                            <span style={{ fontSize: 10, fontWeight: 600, color: '#6C757D', minWidth: 30 }}>{e.progress || 0}%</span>
                          </div>
                        </td>
                        <td style={{ padding: '10px 12px', color: '#ADB5BD', fontSize: 10 }}>{new Date(e.enrolled_date).toLocaleDateString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ─── COURSE MODAL ───────────────────────────────────────────────── */}
      {showCourseModal && (
        <div style={overlayStyle} onClick={() => setShowCourseModal(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div style={modalHeader}>
              <h2 style={modalTitle}>{editingCourse ? 'Edit' : 'Create'} Course</h2>
              <button onClick={() => setShowCourseModal(false)} style={closeBtn}><X size={14} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <input type="text" placeholder="Course Title" value={courseForm.title} onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })} style={inp} />
              <textarea placeholder="Description" value={courseForm.description} onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })} style={{ ...inp, minHeight: 60, resize: 'none' }} />
              <input type="text" placeholder="Category" value={courseForm.category} onChange={(e) => setCourseForm({ ...courseForm, category: e.target.value })} style={inp} />
              <select value={courseForm.access_type} onChange={(e) => setCourseForm({ ...courseForm, access_type: e.target.value })} style={inp}>
                <option value="free">Free</option>
                <option value="paid">Paid</option>
                <option value="subscription">Subscription</option>
              </select>
              {courseForm.access_type !== 'free' && (
                <input type="number" placeholder="Price" value={courseForm.price} onChange={(e) => setCourseForm({ ...courseForm, price: parseFloat(e.target.value) })} style={inp} />
              )}
              <select value={courseForm.status} onChange={(e) => setCourseForm({ ...courseForm, status: e.target.value })} style={inp}>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>

              {/* Module Builder */}
              <div style={{ borderTop: '1px solid #E8EBF0', paddingTop: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <h4 style={{ fontSize: 12, fontWeight: 700, margin: 0, color: '#212529' }}>Modules</h4>
                  <button onClick={addModule} style={btnSm}><Plus size={12} /> Add Module</button>
                </div>
                {courseForm.modules.map(mod => (
                  <div key={mod.id} style={{ background: '#E8EBF0', borderRadius: 8, padding: 10, marginBottom: 10 }}>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                      <input type="text" placeholder="Module title" value={mod.title} onChange={(e) => updateModule(mod.id, 'title', e.target.value)} style={{ ...inp, flex: 1 }} />
                      <button onClick={() => removeModule(mod.id)} style={{ ...btnDanger, flex: 0 }}><X size={12} /></button>
                    </div>
                    {mod.lessons.map(lesson => (
                      <div key={lesson.id} style={{ background: '#F8F9FA', borderRadius: 6, padding: 8, marginBottom: 6, fontSize: 11 }}>
                        <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                          <input type="text" placeholder="Lesson title" value={lesson.title} onChange={(e) => updateLesson(mod.id, lesson.id, 'title', e.target.value)} style={{ ...inp, flex: 1 }} />
                          <button onClick={() => removeLesson(mod.id, lesson.id)} style={{ ...btnDanger, flex: 0 }}><X size={12} /></button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                          <select value={lesson.content_type} onChange={(e) => updateLesson(mod.id, lesson.id, 'content_type', e.target.value)} style={inp}>
                            <option value="video">Video</option>
                            <option value="text">Text</option>
                            <option value="quiz">Quiz</option>
                            <option value="pdf">PDF</option>
                          </select>
                          <input type="text" placeholder="URL" value={lesson.content_url} onChange={(e) => updateLesson(mod.id, lesson.id, 'content_url', e.target.value)} style={inp} />
                          <input type="text" placeholder="Duration" value={lesson.duration} onChange={(e) => updateLesson(mod.id, lesson.id, 'duration', e.target.value)} style={inp} />
                        </div>
                      </div>
                    ))}
                    <button onClick={() => addLesson(mod.id)} style={{ ...btnSm, width: '100%', fontSize: 11 }}><Plus size={11} /> Add Lesson</button>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowCourseModal(false)} style={btnSm}>Cancel</button>
              <button onClick={saveCourse} style={btnPrimary}>Save Course</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── ENROLL MODAL ────────────────────────────────────────────────── */}
      {showEnrollModal && (
        <div style={overlayStyle} onClick={() => setShowEnrollModal(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div style={modalHeader}>
              <h2 style={modalTitle}>Enroll Contact</h2>
              <button onClick={() => setShowEnrollModal(false)} style={closeBtn}><X size={14} /></button>
            </div>
            <select value={enrollForm.contact_id} onChange={(e) => setEnrollForm({ ...enrollForm, contact_id: e.target.value })} style={{ ...inp, marginBottom: 12 }}>
              <option value="">Select Contact</option>
              {contacts.map(c => (
                <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowEnrollModal(false)} style={btnSm}>Cancel</button>
              <button onClick={saveEnrollment} style={btnPrimary}>Enroll</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
